import { defineStore } from 'pinia';
import { auth } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile
} from 'firebase/auth';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    loading: true,
    error: null
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.user,
    userEmail: (state) => state.user?.email,
    uid: (state) => state.user?.uid,
    emailVerified: (state) => state.user?.emailVerified,
    isAdmin: (state) => state.user?.email === 'reserve@kaon-r.jp'
  },
  
  actions: {
    init() {
      return new Promise((resolve) => {
        onAuthStateChanged(auth, (user) => {
          this.user = user;
          this.loading = false;
          resolve(user);
        });
      });
    },
    
    async register(name, email, password) {
      this.loading = true;
      this.error = null;
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // Update display name
        if (name) {
          await updateProfile(userCredential.user, {
            displayName: name
          });
        }
        this.user = userCredential.user;
        await sendEmailVerification(userCredential.user);
        return userCredential.user;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async login(email, password) {
      this.loading = true;
      this.error = null;
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        this.user = userCredential.user;
        return userCredential.user;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async logout() {
      try {
        await signOut(auth);
        this.user = null;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    async resetPassword(email) {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            this.error = error.message;
            throw error;
        }
    },

    async changePassword(newPassword) {
        try {
            if (this.user) {
                await updatePassword(this.user, newPassword);
            } else {
                throw new Error("No user logged in");
            }
        } catch (error) {
            this.error = error.message;
            throw error;
        }
    },

    async reloadUser() {
      try {
        if (this.user) {
          await this.user.reload();
          // Force update the user state to trigger reactivity
          this.user = { ...auth.currentUser };
          return this.user;
        }
      } catch (error) {
        console.error('Error reloading user:', error);
        throw error;
      }
    }
  }
});
